// Declarative table of the per-module conditional show/hide rules for the module CREATE/EDIT forms.
// This logic was previously triplicated across ModuleForm.jsx, ModuleEditForm.jsx, and (the
// submit-side half) validateSpecialForm — and had already drifted. Centralized here, mirroring the
// `dependFields.js` / `overrideFields.js` precedent, so a rule change happens once.
//
// Consumed via `getFieldRenderRules` / `getConditionalWatches` in helpers/formValidation.js. The
// submit-side mutation + required-field enforcement stays in `validateSpecialForm` — this table is
// render-side only.
//
// Shape: SPECIAL_FIELD_RULES[module_code_key][dependent_column_name] = {
//   watch:   the CONTROL column whose value drives this field's visibility,
//   hidden:  (watchedValue, dropdownOptions) => boolean  — pure; reproduces the original predicate,
//   createOnly?: true  — rule applies on the create form only (see the tag divergence below).
// }
//
// IMPORTANT (do not "simplify"):
//   - Compare the dropdown option's `code_key`, NEVER the numeric id.
//   - Preserve the three-state guard exactly: id === null → hidden; id present but the DDO is not yet
//     cached → shown (neither clause fires); DDO cached and code_key !== target → hidden. Collapsing
//     these into one truthiness check changes reveal timing while dropdownOptions is still loading.
export const SPECIAL_FIELD_RULES = {
  // template — email_from only applies when the channel is email.
  template: {
    email_from: {
      watch: 'is_channel_email',
      hidden: (isChannelEmail) => !isChannelEmail,
    },
  },

  // exclusion_list — the "Other" free-text fields appear only when their reason is "Other".
  exclusion_list: {
    other_reason: {
      watch: 'exclusion_reason_dropdown_option_id',
      hidden: (reasonId, dropdownOptions) => reasonId === null
        || (!!dropdownOptions[reasonId] && dropdownOptions[reasonId].code_key !== 'exclusion_reason_other'),
    },
    other_requested_by: {
      watch: 'exclusion_requested_by_dropdown_option_id',
      hidden: (requestedById, dropdownOptions) => requestedById === null
        || (!!dropdownOptions[requestedById] && dropdownOptions[requestedById].code_key !== 'exclusion_requested_by_other'),
    },
  },

  // payment — discount_reason_other appears only when the discount reason is "Other".
  payment: {
    discount_reason_other: {
      watch: 'discount_reason_dropdown_option_id',
      hidden: (discountReasonId, dropdownOptions) => discountReasonId === null
        || (!!dropdownOptions[discountReasonId] && dropdownOptions[discountReasonId].code_key !== 'discount_reason_waive_other'),
    },
  },

  // tag — DIVERGENT create vs edit, preserved (NOT yet unified — a deliberate product decision, not
  // yet made):
  //   create: should_create_tag_in_rc is shown; rc_tag_id is hidden when it is checked (rule below).
  //   edit:   should_create_tag_in_rc is hard-hidden and rc_tag_id is always shown — handled in
  //           ModuleEditForm's own loop, NOT here. `createOnly` keeps this rule off the edit form.
  tag: {
    rc_tag_id: {
      watch: 'should_create_tag_in_rc',
      hidden: (shouldCreateTagInRc) => !!shouldCreateTagInRc,
      createOnly: true,
    },
  },
}
